import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { getPayoutBalances } from "@/lib/api";

const fmt = (v) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const RailChip = ({ label, sym, data, testId }) => (
  <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-white rounded-md border" data-testid={testId}>
    <span className="text-xs font-medium text-slate-600">{label}</span>
    {data?.ok ? (
      <span className="flex items-center gap-2">
        <span className="font-mono font-semibold text-emerald-700">{sym}{fmt(data.balance)}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase ${data.mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {data.mode}
        </span>
      </span>
    ) : (
      <span className="text-xs text-slate-400" title={data?.error || ""}>unavailable</span>
    )}
  </div>
);

// Region-scoped: the UK portal only ever fetches/shows the GoCardless float,
// the AUS portal only the Capay float. Mode (sandbox/live) always mirrors the
// real disbursement setting from Admin Settings.
const usePayoutBalances = () => {
  const { user } = useAuth();
  const { currentRegion } = useRegion();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const canView = Boolean(user?.can_view_payout_balance || user?.is_dev_bypass);
  const isAus = currentRegion === "AUS";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getPayoutBalances(isAus ? "AUS" : "UK"));
      setLoadError(null);
    } catch (e) {
      setData(null);
      setLoadError(e?.response?.data?.detail || "Couldn't load balance — retry");
    } finally {
      setLoading(false);
    }
  }, [isAus]);

  return { canView, isAus, data, loadError, loading, load };
};

const RegionRail = ({ isAus, data }) =>
  isAus ? (
    <RailChip label="Capay (AUS)" sym="A$" data={data?.capay} testId="payout-balance-capay" />
  ) : (
    <RailChip label="GoCardless (UK)" sym="£" data={data?.gc} testId="payout-balance-gc" />
  );

// "Check Balance" button + popover — lives under the Sync Monday button on
// the Credit Approved Deals page. Only rendered for flagged users.
export const CheckBalanceButton = () => {
  const { canView, isAus, data, loadError, loading, load } = usePayoutBalances();
  const [open, setOpen] = useState(false);

  if (!canView) return null;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="check-balance-btn">
          <Wallet className="w-4 h-4" /> Check Balance
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3 space-y-2" data-testid="check-balance-popover">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold flex items-center gap-1.5 text-slate-800">
            <Wallet className="w-4 h-4 text-blue-600" /> Payout Float
          </span>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={load} disabled={loading} data-testid="payout-balance-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        {loading && !data ? (
          <div className="text-xs text-muted-foreground py-2" data-testid="payout-balance-loading">Checking balance…</div>
        ) : (
          <RegionRail isAus={isAus} data={data} />
        )}
        {loadError && (
          <div className="text-xs text-rose-600" data-testid="payout-balance-error">{loadError}</div>
        )}
        {data?.as_of && (
          <div className="text-[10px] text-muted-foreground">as of {new Date(data.as_of).toLocaleTimeString("en-GB")}</div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// Inline banner variant (kept for reuse on other pages if needed).
export const PayoutBalanceCard = () => {
  const { canView, isAus, data, loadError, loading, load } = usePayoutBalances();

  useEffect(() => { if (canView) load(); }, [canView, load]);

  if (!canView) return null;
  return (
    <div className="mb-4 flex items-center gap-3 flex-wrap p-3 bg-slate-50 rounded-lg border" data-testid="payout-balance-card">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
        <Wallet className="w-4 h-4 text-blue-600" /> Payout Float
      </div>
      <RegionRail isAus={isAus} data={data} />
      {loadError && (
        <span className="text-xs text-rose-600" data-testid="payout-balance-error">{loadError}</span>
      )}
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={load} disabled={loading} data-testid="payout-balance-refresh">
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
      </Button>
      {data?.as_of && (
        <span className="text-[10px] text-muted-foreground">as of {new Date(data.as_of).toLocaleTimeString("en-GB")}</span>
      )}
    </div>
  );
};
